import { useState, type FormEvent } from 'react';
import { Anchor, Button, Container, Fieldset, Stack, Text, TextInput, Title } from '@mantine/core';
import { Link } from 'react-router-dom';
import { api, errorMessage } from '../api/client';
import { ErrorNote } from '../components/ErrorNote';

/**
 * Asks for an email address and sends a reset link.
 *
 * The confirmation is the same whether or not there is an account for the address. Saying which
 * would tell anyone with the form who banks with us, so the wording never narrows it down.
 */
export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setProblem(null);
    setBusy(true);
    try {
      await api.requestPasswordReset(email.trim());
      setSent(true);
    } catch (error) {
      setProblem(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <Container component="main" size={460} pt={80} pb={64}>
        <Title order={1}>Check your email</Title>
        <Text size="lg" mt="sm">
          If there is an account for that address, we have sent a link to reset the password. The
          link works for one hour.
        </Text>
        <Text mt="md">Nothing arrived? Look in your spam folder, or try the form again.</Text>
        <Text c="dimmed" size="sm" mt="lg">
          <Anchor component={Link} to="/login" size="sm">
            Back to sign in
          </Anchor>
        </Text>
      </Container>
    );
  }

  return (
    <Container component="main" size={460} pt={80} pb={64}>
      <Title order={1}>Forgotten your password?</Title>
      <Text size="lg" mt="sm" mb="lg">
        Give us the email address you sign in with and we will send you a link to set a new
        password.
      </Text>
      {problem !== null && <ErrorNote message={problem} />}
      <form onSubmit={(event) => void submit(event)}>
        <Fieldset variant="unstyled" disabled={busy}>
          <Stack gap="md">
            <TextInput
              id="reset-email"
              label="Email address"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            <Button type="submit" mt="xs">
              {busy ? 'Sending...' : 'Send the link'}
            </Button>
          </Stack>
        </Fieldset>
      </form>
      <Text c="dimmed" size="sm" mt="lg">
        Remembered it?{' '}
        <Anchor component={Link} to="/login" size="sm">
          Sign in
        </Anchor>
        .
      </Text>
    </Container>
  );
}
